import {
  Pressable,
  type StyleProp,
  StyleSheet,
  View,
  type ViewStyle,
} from "react-native"
import { Tiny } from "@/components/standard/BFText"
import { BFColors } from "@/constants/Colors"
import GetLoanButtonIcon from "@/assets/icons/get-loan-button-icon.svg"
import MapIcon from "@/assets/icons/map-icon.svg"
import { useCreateLoan } from "@/hooks/useCreateLoan"
import { useCallback } from "react"
import { loanButtonClicked } from "@/analytics/events/analyzer"
import { useScreenName } from "@/hooks/useScreenName"

interface CircularFloatingButtonProps {
  type: "getLoan" | "viewMap"
  analysisId: string
  onPress?: () => void
  style?: StyleProp<ViewStyle>
}

const CircularFloatingButton: React.FC<CircularFloatingButtonProps> = ({
  type,
  analysisId,
  onPress,
  style = {},
}: CircularFloatingButtonProps) => {
  const { createLoan, isPending } = useCreateLoan(analysisId)
  const screenName = useScreenName()

  const handleGetLoanPress = useCallback(() => {
    loanButtonClicked({ currentScreen: screenName })
    if (!isPending && createLoan) {
      createLoan()
    }
  }, [isPending, createLoan, screenName])

  return (
    <Pressable
      testID={type === "getLoan" ? "get-a-loan-button" : "view-map-button"}
      onPress={type === "getLoan" ? handleGetLoanPress : onPress}
      cssInterop={false}
      style={({ pressed }) => [
        styles.floatingButton,
        isPending && styles.disabledButton, // Apply disabled styles when isPending
        pressed && !isPending && styles.pressedButton, // Only apply pressed styles if not pending
        style,
        pressed && styles.pressedButton,
      ]}
    >
      <View className="flex-col items-center">
        {type === "getLoan" ? (
          <>
            <GetLoanButtonIcon />
            <Tiny
              className="mt-1"
              style={{
                color: isPending ? BFColors.calmBlue : BFColors.lightest,
              }}
            >
              {"Get Loan"}
            </Tiny>
          </>
        ) : (
          <>
            <MapIcon width={20} height={26} />
            <Tiny className="text-white mt-0">{"View Map"}</Tiny>
          </>
        )}
      </View>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  floatingButton: {
    fontFamily: "Figtree",
    backgroundColor: BFColors.midnightBlue,
    width: 74,
    height: 74,
    borderRadius: 40,
    justifyContent: "center",
    alignItems: "center",
    elevation: 5,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    zIndex: 3,
  },
  pressedButton: {
    elevation: 2,
    shadowOffset: {
      width: 1,
      height: 1,
    },
    shadowOpacity: 0.2, // Slightly less opacity for the shadow
  },
  disabledButton: {
    backgroundColor: BFColors.lightGrey,
  },
})

export default CircularFloatingButton
